import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSupportStore, type ReportKind } from '../stores/supportStore';
import { modalStyles } from './CreateProjectModal';
import { theme } from '../theme';

const MAX_MESSAGE = 4000;

const failureText = {
  expired: 'Your sign-in has expired. Log in again in another tab, then press Send again.',
  failed: "That didn't send. Check your connection and try again.",
};

export function ReportProblemModal({
  projectId,
  onClose,
}: {
  projectId?: string;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<ReportKind>('bug');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const { sending, sendReport } = useSupportStore();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!message.trim() || sending) return;
    setError('');
    const result = await sendReport({ kind, message: message.trim(), projectId });
    if (result === 'sent') {
      setSent(true);
      setTimeout(onClose, 1500);
      return;
    }
    const serverError = useSupportStore.getState().error;
    if (result === 'expired') setError(failureText.expired);
    else setError(serverError ?? failureText.failed);
  };

  return (
    <div style={modalStyles.overlay} onClick={message.trim() ? undefined : onClose}>
      <div style={{ ...modalStyles.modal, maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.title}>Report a problem</div>
        {sent ? (
          <div style={{ color: theme.success, fontSize: '14px' }}>Thanks, we got it.</div>
        ) : (
          <form onSubmit={onSubmit}>
            <div style={{ ...modalStyles.hint, marginTop: 0, marginBottom: '16px' }}>
              Already on the list?{' '}
              <Link to="/known-issues" style={{ color: theme.accent }} onClick={onClose}>
                Check the known issues
              </Link>
            </div>
            <div style={modalStyles.field}>
              <label style={modalStyles.label}>
                <input
                  type="radio"
                  name="report-kind"
                  checked={kind === 'bug'}
                  onChange={() => setKind('bug')}
                />{' '}
                Bug
              </label>
              <label style={modalStyles.label}>
                <input
                  type="radio"
                  name="report-kind"
                  checked={kind === 'idea'}
                  onChange={() => setKind('idea')}
                />{' '}
                Idea
              </label>
            </div>
            <div style={modalStyles.field}>
              <label style={modalStyles.label} htmlFor="report-message">
                {kind === 'bug' ? 'What happened, and what were you doing?' : 'What would help?'}
              </label>
              <textarea
                id="report-message"
                style={{ ...modalStyles.input, minHeight: '140px', resize: 'vertical' }}
                maxLength={MAX_MESSAGE}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                autoFocus
              />
              <div style={modalStyles.hint}>
                {message.length} / {MAX_MESSAGE}. The page you&apos;re on and your browser are sent
                with it.
              </div>
            </div>
            {error && <div style={modalStyles.error}>{error}</div>}
            <div style={modalStyles.actions}>
              <button type="button" style={modalStyles.btn} onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  ...modalStyles.btnPrimary,
                  opacity: sending || !message.trim() ? 0.6 : 1,
                }}
                disabled={sending || !message.trim()}
              >
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
